import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	OnInit,
	output
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { ReviewEntriesService } from '@app/pages/review-entries/review-entries.service';
import { DatepickerComponent } from '@app/shared/datepicker/datepicker.component';
import { InputDropdownComponent } from '@app/shared/input-dropdown/input-dropdown.component';
import { SelectCheckboxComponent } from '@app/shared/select-checkbox/select-checkbox.component';
import { AuthService } from '@core/services/auth.service';
import { HomeStateService } from '@core/services/home-state.service';
import { HomeService } from '@pages/home/home.service';
import {
	ConfirmDialogComponent,
	ConfirmDialogModel
} from '@shared/confirm-dialog/confirm-dialog.component';
import { SharedModule } from '@shared/shared.module';
import { isEqual } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { DatepickerDialogComponent } from '../../datepicker-dialog/datepicker-dialog.component';
import { ProfileService } from '../services/profile.service';
import { RestrictedPayType } from './../services/profile.service';
import { AddHolidaysComponent } from './add-holidays/add-holidays.component';
import { PaytypeWeekdayDialogComponent } from './paytype-weekday-dialog/paytype-weekday-dialog.component';

export interface Paytype {
	PayType: string;
}

@Component({
	selector: 'maintenance',
	imports: [
		CommonModule,
		MaterialModule,
		FormsModule,
		ReactiveFormsModule,
		SharedModule,
		SelectCheckboxComponent,
		InputDropdownComponent,
		DatepickerComponent
	],
	templateUrl: './maintenance.component.html',
	styleUrl: './maintenance.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MaintenanceComponent implements OnInit {
	maintenanceForm!: FormGroup;

	formStatus = output<string>();
	citiworksIntegration = output<boolean>();
	ptoIntegration = output<boolean>();
	cityworksAdminUserListUpdated = output<string>(); // Emit when CityworksAdminUserList is updated after save
	// firstnameSignal: Signal<any> | undefined;
	moduleControlMaster: any = {};
	timeCardSetting: any = {};
	holidaysList: any[] = [];

	initialFormValues: any; // keep track of any changes

	private readonly homeService: HomeService = inject(HomeService);
	private readonly reviewService: ReviewEntriesService =
		inject(ReviewEntriesService);
	employeeList: any[] = [];
	trainingAdminEmpIdFromMaster: string = '';

	weekdays = [
		{ name: 'Su', FullName: 'Sunday', value: 0, checked: false },
		{ name: 'Mo', FullName: 'Monday', value: 1, checked: false },
		{ name: 'Tu', FullName: 'Tuesday', value: 2, checked: false },
		{ name: 'We', FullName: 'Wednesday', value: 3, checked: false },
		{ name: 'Th', FullName: 'Thursday', value: 4, checked: false },
		{ name: 'Fr', FullName: 'Friday', value: 5, checked: false },
		{ name: 'Sa', FullName: 'Saturday', value: 6, checked: false }
	];

	employeeUnionUnits: any[] = [];

	allPaytypes: Paytype[] = [];
	restrictedPayTypes: RestrictedPayType[] = [];
	w2PrivilegedEmployees: any[] = [];

	constructor(
		private readonly fb: FormBuilder,
		private readonly dialog: MatDialog,
		private readonly authService: AuthService,
		private readonly toastr: ToastrService,
		private readonly cdr: ChangeDetectorRef,
		private readonly profileService: ProfileService,
		private readonly homeStateService: HomeStateService
	) {
		this.maintenanceForm = this.fb.group({
			// RMS Module Control
			GLDepartment: [false],
			FirstPayrollStartDate: [''],
			ShowAttachments: [false],
			AfterDate: [''],
			InventoryLockedAfterDate: [''],
			BenefitsLockedAfterDate: [''],
			BenefitsLockedMessage: [''],
			Message: [''],
			ShowAdditionalHourlyRateApplies: [false],
			EmployeeLogOff: [''],
			SupervisorLogOff: [''],
			InventoryLockedMessage: [''],
			TimeClock: [false],
			TimeClockTotal: [false],
			TimeClockDecimal: [false],
			Inventory: [false],
			InventoryReleaseApproval: [false],
			TimeClockDecimalTenths: [false],
			CalendarYears: [false],
			AllowChangePassword: [false],
			APIKey: [''],
			SecondApproval: [false],
			ComplexPassword: [false],
			ResetPasswordDays: [''],
			AdditionalRate: [false],
			AdditionalRateRequested: [false],
			useLocationServices: [false],
			useCombinedPayTypeEntry: [false],
			IsTraining: [false],
			TrainingAdminEmpId: [],
			CityworksAdminUserList: [],
			IsDailyNotification: [false],
			IsWeeklyNotification: [false],
			DailyNotificationRunDateTime: [''],
			WeeklyNotificationRunDateTime: [''],
			RegularPayTypeAlias: [''],
			CopyDetailsDefaultInvCheck: [false],
			CopyDetailsDefaultEquipCheck: [false],
			Cityworks: [false],
			CityworksAPIKEY: [''],
			NotificationUnionUnits: [],
			IsPTO: [false],
			IsReviewBulkAction: [false],
			GLLookUpEmployeeList: [''],

			// Time Card Settings
			LaborWarningHours: [''],
			EquipmentMaxHours: [''],
			IsLaborHoursWarning: [false],
			IsLaborUpdateEquipmentHoursWarning: [false],
			IsEquipmentMaxHoursWarning: [false],
			IsEquipmentandLaborHoursWarning: [false],
			IsAllowReviewersSubmit: [false],
			IsEquipmentNotLaborHoursWarning: [false],
			LoginScreenShowAll: [false],
			IsLogDuplicateTokens: [false],
			ConcurrentLogin: [false],
			IsSequenceIncrements: [false],
			IsEmpSummaryJoClassificationToggle: [false],
			IsEmpSummaryExpandAllToggle: [false],
			IsEmpSummaryTrimGLToggle: [false],
			IsConsecutiveLoginFailedLock: [false],
			LockEMailAddress: [''],
			IsReviewShowTimecard: [false],
			IsMinimumThresholdAlerts: [false],
			TimeCardLockEmployeeList: [''],
			CopyDetailsNotAllowPayTypes: [''],
			AllowZeroLaborHours: [false],
			IsItemMaster: [false],
			IsInventory: [false],
			IsPurchaseOrder: [false],
			ConflictingPayTypes: [''],
			PayHoursRestrictedValues: [''],
			IsInsufficientClockedInTimeIndicator: [false],
			InsufficientClockedInTimeIndicatorThreshold: [null],
			IsDefaultShiftUnEditable: [false],
			IsShowHomeBenefitBalances: [false],
			IsShowReviewBenefitBalances: [false],
			W2ReportEnabled: [false],
			/** When true, W-2 report allows any tax year; when false (default), only the prior calendar year. */
			W2AllowAnyTaxYear: [false],
			W2PrivilegedEmployeeList: [''],
			IsActivity517flow: [false],
			IsVUEWorksAPIEnabled: [false],
			VUEWorksAPIUrl: ['']
			// PayHoursRestrictedMultiple: [false],
			// PayHoursRestrictedMultipleValue: ['']
		});

		// Listen to form changes to check if it's touched or dirty
		this.maintenanceForm.valueChanges.subscribe(() => {
			this.checkFormStatus();
		});
	}

	ngOnInit(): void {
		this.getAllEmployees();
		this.getEmployeeUnionUnits();
		this.getAllPayTypes();
		this.getPayTypeHoursRestrictedList();
		this.getTCKModuleControlMaster();
		this.getTCKTimeCardSettings();
		this.getHolidaysObservedList();

		// Save the initial form values when the form is initialized
		this.initialFormValues = this.maintenanceForm.value;

		// Subscribe to form value changes to monitor if the form reverts back to its original state
		this.maintenanceForm.valueChanges.subscribe(() => {
			this.checkFormState();
		});
	}

	// Add this method to handle checkbox changes
	onCheckboxChange(controlName: string, event: any): void {
		const checked = event.target.checked;

		// Get the current form control
		const control = this.maintenanceForm.get(controlName);
		if (control) {
			// Update the form control value directly
			control.setValue(checked);
			control.markAsDirty();
			control.updateValueAndValidity();

			// Also update the form state
			this.maintenanceForm.markAsDirty();
			this.maintenanceForm.updateValueAndValidity();
		}
	}

	checkFormState() {
		// Compare current form values with the initial values
		const currentValues = this.maintenanceForm.value;

		if (isEqual(currentValues, this.initialFormValues)) {
			// If the values match, mark the form as pristine and untouched
			this.maintenanceForm.markAsPristine();
			this.maintenanceForm.markAsUntouched();
		}
	}

	getAllEmployees() {
		this.reviewService.getAllEmployees().subscribe({
			next: (d) => {
				this.employeeList = d.map((item: any) => ({
					value: item.EmployeeNumber,
					label: `${item.LastName}, ${item.FirstName} (${item.EmployeeNumber})`
				}));

				// If module control data has already been loaded, update the form
				if (this.trainingAdminEmpIdFromMaster) {
					this.updateTrainingAdminEmpIdInForm();
				}

				// If timecard settings have already been loaded, update the GL lookup form
				if (this.timeCardSetting?.GLLookUpEmployeeList) {
					this.updateGLLookUpEmployeeListInForm();
				}

				// If timecard settings have already been loaded, update the Cityworks admin user list
				if (
					this.timeCardSetting?.CityworksAdminUserList !== undefined
				) {
					this.updateCityworksAdminUserListInForm();
				}

				// If w2 privileged employees have already been loaded, update the form
				this.w2PrivilegedEmployees = this.employeeList;
				if (this.timeCardSetting?.W2PrivilegedEmployeeList) {
					this.updateW2PrivilegedEmployeeListInForm();
				}
			},
			error: (error) => {
				console.log('get all employees error : ', error);
			}
		});
	}

	getEmployeeUnionUnits() {
		this.profileService.getEmployeeUnionUnits().subscribe({
			next: (d) => {
				this.employeeUnionUnits = d.map((item: any) => ({
					value: item.Id,
					label: item.UnionUnit
				}));

				// Update NotificationUnionUnits if timecard settings are already loaded
				this.updateNotificationUnionUnits();
			},
			error: (error) => {
				console.log('get employee union units error : ', error);
			}
		});
	}

	updateTrainingAdminEmpIdInForm() {
		if (this.employeeList.length > 0) {
			if (this.trainingAdminEmpIdFromMaster) {
				let trainingAdminEmpId = this.trainingAdminEmpIdFromMaster
					.split(',')
					.map((id) => Number(id));
				this.maintenanceForm.patchValue({
					TrainingAdminEmpId: this.employeeList.filter((item: any) =>
						trainingAdminEmpId.includes(Number(item.value))
					)
				});
			} else {
				// Set to empty array if trainingAdminEmpIdFromMaster is null/empty
				this.maintenanceForm.patchValue({
					TrainingAdminEmpId: []
				});
			}
			this.cdr.detectChanges();
		}
	}

	updateCityworksAdminUserListInForm() {
		if (this.employeeList.length > 0) {
			if (this.timeCardSetting?.CityworksAdminUserList) {
				let cityworksAdminUserList =
					this.timeCardSetting.CityworksAdminUserList.split(',').map(
						(id: string) => Number(id.trim())
					);
				this.maintenanceForm.patchValue({
					CityworksAdminUserList: this.employeeList.filter(
						(item: any) =>
							cityworksAdminUserList.includes(Number(item.value))
					)
				});
			} else {
				// Set to empty array if CityworksAdminUserList is null/empty
				this.maintenanceForm.patchValue({
					CityworksAdminUserList: []
				});
			}
			this.cdr.detectChanges();
		}
	}

	updateGLLookUpEmployeeListInForm() {
		if (
			this.employeeList.length > 0 &&
			this.timeCardSetting?.GLLookUpEmployeeList
		) {
			let glLookUpEmployeeIds =
				this.timeCardSetting.GLLookUpEmployeeList.split(',')
					.map((id: string) => Number(id.trim()))
					.filter((id: number) => !isNaN(id)); // Filter out any invalid numbers
			this.maintenanceForm.patchValue({
				GLLookUpEmployeeList: this.employeeList.filter((item: any) =>
					glLookUpEmployeeIds.includes(Number(item.value))
				)
			});
			this.cdr.detectChanges();
		}
	}

	updateConflictingPayTypesInForm() {
		if (
			this.allPaytypes.length > 0 &&
			this.timeCardSetting?.ConflictingPayTypes
		) {
			let paytypes = this.timeCardSetting.ConflictingPayTypes.split(
				','
			).map((val: string) => val.trim()); // Filter out any invalid numbers
			this.maintenanceForm.patchValue({
				ConflictingPayTypes: this.allPaytypes.filter((item: any) =>
					paytypes.includes(item.value)
				)
			});
			this.cdr.detectChanges();
		}
	}

	updateW2PrivilegedEmployeeListInForm() {
		if (
			this.w2PrivilegedEmployees.length > 0 &&
			this.timeCardSetting?.W2PrivilegedEmployeeList
		) {
			const employeeIds = this.timeCardSetting.W2PrivilegedEmployeeList
				.split(',')
				.map((id: string) => Number(id.trim()))
				.filter((id: number) => !isNaN(id));
			this.maintenanceForm.patchValue({
				W2PrivilegedEmployeeList: this.w2PrivilegedEmployees.filter(
					(item: any) => employeeIds.includes(Number(item.value))
				)
			});
			this.cdr.detectChanges();
		}
	}

	updatePayHoursRestrictedValuesInForm() {
		// Only update if we have the restricted pay types loaded and saved data exists
		if (
			this.restrictedPayTypes.length > 0 &&
			this.timeCardSetting?.PayHoursRestrictedValues
		) {
			// Parse the saved data (format could be: "PayType1:10,PayType2:20" or similar)
			// Adjust the parsing logic based on your actual API format
			const savedValues = this.timeCardSetting.PayHoursRestrictedValues;

			// Example: If the API returns a comma-separated string like "REG:8,OT:10"
			// Parse it into an array of objects
			const parsedValues = savedValues.split(',').map((item: string) => {
				const [payType, hours] = item
					.split(':')
					.map((v: string) => v.trim());
				return { payType, hours: parseFloat(hours) || 0 };
			});

			// Find the matching options from restrictedPayTypes and create the proper format
			const formattedValues = parsedValues
				.map((saved: any) => {
					const matchingOption = this.restrictedPayTypes.find(
						(opt: RestrictedPayType) => opt.value === saved.payType
					);
					if (matchingOption) {
						return {
							option: matchingOption,
							inputValue: saved.hours
						};
					}
					return null;
				})
				.filter((item: any) => item !== null);

			// Set the form value
			this.maintenanceForm.patchValue({
				PayHoursRestrictedValues: formattedValues
			});
			this.cdr.detectChanges();
		} else if (
			this.restrictedPayTypes.length > 0 &&
			!this.timeCardSetting?.PayHoursRestrictedValues
		) {
			// Alternative: If the API returns enabled pay types separately, use the Enable and PayHours from the options
			// This auto-populates based on Enable flag
			const enabledOptions = this.restrictedPayTypes
				.filter((opt: RestrictedPayType) => opt.Enable)
				.map((opt: RestrictedPayType) => ({
					option: opt,
					inputValue: opt.PayHours || 0
				}));

			if (enabledOptions.length > 0) {
				this.maintenanceForm.patchValue({
					PayHoursRestrictedValues: enabledOptions
				});
				this.cdr.detectChanges();
			}
		}
	}

	getTCKModuleControlMaster() {
		this.homeService.getTCKModuleControlMasterForMaintenance().subscribe({
			next: (response) => {
				this.moduleControlMaster = response;
				this.maintenanceForm.patchValue({
					...this.moduleControlMaster
				});

				this.citiworksIntegration.emit(response.Cityworks);

				// Multiple delayed emissions with increasing delays to ensure parent component catches it
				setTimeout(() => {
					this.citiworksIntegration.emit(response.Cityworks);
				}, 100);

				setTimeout(() => {
					this.citiworksIntegration.emit(response.Cityworks);
				}, 300);

				setTimeout(() => {
					this.citiworksIntegration.emit(response.Cityworks);
				}, 500);

				// Store the training admin emp IDs and update the form if employees are loaded
				this.trainingAdminEmpIdFromMaster =
					this.moduleControlMaster.TrainingAdminEmpId;
				this.updateTrainingAdminEmpIdInForm();
				this.updateCityworksAdminUserListInForm();

				// Update GL lookup form if employees are loaded
				this.updateGLLookUpEmployeeListInForm();
			},
			error: (error) => {
				this.citiworksIntegration.emit(false);
			}
		});
	}
	getTCKTimeCardSettings() {
		this.homeService.getTCKTimeCardSettingsForMaintenance().subscribe({
			next: (response) => {
				this.timeCardSetting = response;
				// Exclude fields that need special handling from patchValue
				const {
					CityworksAdminUserList,
					GLLookUpEmployeeList,
					W2PrivilegedEmployeeList,
					...restOfSettings
				} = this.timeCardSetting;
				this.maintenanceForm.patchValue({ ...restOfSettings });
				this.ptoIntegration.emit(response.IsPTO);

				if (
					this.timeCardSetting.WorkWeekDays &&
					this.timeCardSetting.WorkWeekDays.length > 0
				) {
					let selectedDays = this.timeCardSetting.WorkWeekDays.split(
						','
					).map(function (a: any) {
						return parseInt(a);
					});
					this.weekdays.forEach(function (day: any) {
						day.checked = selectedDays.includes(day.value + 1);
					});
				}

				// Update NotificationUnionUnits if employee union units are already loaded
				this.updateNotificationUnionUnits();

				// Update GL lookup form if employees are loaded
				this.updateGLLookUpEmployeeListInForm();

				// Update Cityworks admin user list if employees are loaded
				this.updateCityworksAdminUserListInForm();

				// Update GL lookup form if employees are loaded
				this.updateConflictingPayTypesInForm();

				// Update W2 Privileged Employee List if employees are loaded
				this.updateW2PrivilegedEmployeeListInForm();

				// Update restricted pay types with values
				this.updatePayHoursRestrictedValuesInForm();
			},
			error: (error) => {
				console.log('get timecard setting error : ', error);
			}
		});
	}

	updateNotificationUnionUnits() {
		if (
			this.timeCardSetting &&
			this.timeCardSetting.NotificationUnionUnits &&
			this.employeeUnionUnits &&
			this.employeeUnionUnits.length > 0
		) {
			const selectedUnion = this.employeeUnionUnits.filter((x: any) =>
				this.timeCardSetting.NotificationUnionUnits.split(',').includes(
					x.label
				)
			);
			if (selectedUnion.length > 0) {
				this.maintenanceForm.patchValue({
					NotificationUnionUnits: selectedUnion
				});
				this.cdr.detectChanges();
			}
		}
	}

	getHolidaysObservedList() {
		this.homeService.getHolidaysObservedList().subscribe({
			next: (response) => {
				this.holidaysList = response;
			},
			error: (error) => {
				console.log('get timecard setting error : ', error);
			}
		});
	}

	openPayrollStartCalendar(): void {
		const dialogRef = this.dialog.open(DatepickerDialogComponent, {
			width: '450px', // Set the desired width
			panelClass: 'custom-sml-calendar',
			data: {
				showEvents: false
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle any actions after the dialog is closed
			if (!!result && result.date) {
				this.maintenanceForm.patchValue({
					FirstPayrollStartDate: result.date
				});
			}
		});
	}

	openAfterDateCalendar(): void {
		const dialogRef = this.dialog.open(DatepickerDialogComponent, {
			width: '450px', // Set the desired width
			panelClass: 'custom-sml-calendar',
			data: {
				showEvents: false
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle any actions after the dialog is closed
			if (!!result && result.date) {
				this.maintenanceForm.patchValue({
					AfterDate: result.date
				});
			}
		});
	}

	openInventoryLockedAfterDateCalendar(): void {
		const dialogRef = this.dialog.open(DatepickerDialogComponent, {
			width: '450px', // Set the desired width
			panelClass: 'custom-sml-calendar',
			data: {
				showEvents: false
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle any actions after the dialog is closed
			if (!!result && result.date) {
				this.maintenanceForm.patchValue({
					InventoryLockedAfterDate: result.date
				});
			}
		});
	}

	openDailyNotificationRunDateTimeCalendar(): void {
		const dialogRef = this.dialog.open(DatepickerDialogComponent, {
			width: '450px', // Set the desired width
			panelClass: 'custom-sml-calendar',
			data: {
				showEvents: false
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle any actions after the dialog is closed
			if (!!result && result.date) {
				this.maintenanceForm.patchValue({
					DailyNotificationRunDateTime: result.date
				});
			}
		});
	}

	openBenefitsLockedAfterDateCalendar(): void {
		const dialogRef = this.dialog.open(DatepickerDialogComponent, {
			width: '450px', // Set the desired width
			panelClass: 'custom-sml-calendar',
			data: {
				showEvents: false
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle any actions after the dialog is closed
			if (!!result && result.date) {
				this.maintenanceForm.patchValue({
					BenefitsLockedAfterDate: result.date
				});
			}
		});
	}

	openWeeklyNotificationRunDateTimeCalendar(): void {
		const dialogRef = this.dialog.open(DatepickerDialogComponent, {
			width: '450px', // Set the desired width
			panelClass: 'custom-sml-calendar',
			data: {
				showEvents: false
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle any actions after the dialog is closed
			if (!!result && result.date) {
				this.maintenanceForm.patchValue({
					WeeklyNotificationRunDateTime: result.date
				});
			}
		});
	}

	// Clear date methods for all date fields
	clearFirstPayrollStartDate(): void {
		this.maintenanceForm.patchValue({
			FirstPayrollStartDate: ''
		});
		this.maintenanceForm.get('FirstPayrollStartDate')?.markAsDirty();
	}

	clearAfterDate(): void {
		this.maintenanceForm.patchValue({
			AfterDate: ''
		});
		this.maintenanceForm.get('AfterDate')?.markAsDirty();
	}

	clearInventoryLockedAfterDate(): void {
		this.maintenanceForm.patchValue({
			InventoryLockedAfterDate: ''
		});
		this.maintenanceForm.get('InventoryLockedAfterDate')?.markAsDirty();
	}

	clearBenefitsLockedAfterDate(): void {
		this.maintenanceForm.patchValue({
			BenefitsLockedAfterDate: ''
		});
		this.maintenanceForm.get('BenefitsLockedAfterDate')?.markAsDirty();
	}

	clearDailyNotificationRunDateTime(): void {
		this.maintenanceForm.patchValue({
			DailyNotificationRunDateTime: ''
		});
		this.maintenanceForm.get('DailyNotificationRunDateTime')?.markAsDirty();
	}

	clearWeeklyNotificationRunDateTime(): void {
		this.maintenanceForm.patchValue({
			WeeklyNotificationRunDateTime: ''
		});
		this.maintenanceForm
			.get('WeeklyNotificationRunDateTime')
			?.markAsDirty();
	}

	addHolidays(): void {
		const dialogRef = this.dialog.open(AddHolidaysComponent, {
			width: '450px', // Set the desired width
			panelClass: 'custom-sml-calendar',
			data: {
				showEvents: false,
				isEdit: false
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle any actions after the dialog is closed
			if (result === 'saved') {
				this.getHolidaysObservedList();
				this.toastr.success('Holiday ADDED successfully.', 'Success', {
					positionClass: 'toast-bottom-left',
					toastClass: 'toast-icon custom-toast-success'
				});
			}
		});
	}

	onUpdateHoliday(item: any): void {
		const dialogRef = this.dialog.open(AddHolidaysComponent, {
			width: '450px', // Set the desired width
			panelClass: 'custom-sml-calendar',
			data: {
				showEvents: false,
				isEdit: true,
				item: item
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle any actions after the dialog is closed
			if (result === 'saved') {
				this.getHolidaysObservedList();
				this.toastr.success(
					'Holiday UPDATED successfully.',
					'Success',
					{
						positionClass: 'toast-bottom-left',
						toastClass: 'toast-icon custom-toast-success'
					}
				);
			}
		});
	}

	deleteHoliday(id: number): void {
		const message = `Are you sure you want to delete this record?`;

		const dialogData = new ConfirmDialogModel('Confirm Delete', message);

		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			// hasBackdrop: false,
			maxWidth: '400px',
			data: dialogData
		});
		dialogRef.afterClosed().subscribe((dialogResult) => {
			if (dialogResult && id) {
				this.homeService.deleteHolidaysObserved(id).subscribe({
					next: (response) => {
						this.holidaysList = this.holidaysList.filter(
							(item: any) => item.HolidayId !== id
						);
						this.toastr.success(
							'Holiday DELETED successfully.',
							'Success',
							{
								positionClass: 'toast-bottom-left',
								toastClass: 'toast-icon custom-toast-success'
							}
						);
					},
					error: (error) => {
						console.log('Error to delete holiday : ', error);
					}
				});
			}
		});
	}

	openPaytypeWeekdayDialog(day: any): void {
		// Map day value (0-6) to DayNo (1-7) as per API structure
		// API uses: 1=Su, 2=Mo, 3=Tu, 4=We, 5=Th, 6=Fr, 7=Sa
		// Component uses: 0=Su, 1=Mo, 2=Tu, 3=We, 4=Th, 5=Fr, 6=Sa
		const dayNo = day.value + 1;
		const dialogRef = this.dialog.open(PaytypeWeekdayDialogComponent, {
			width: '500px',
			panelClass: 'custom-sml-calendar',
			data: {
				dayName: day.FullName,
				dayNo: dayNo
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			// Handle any actions after the dialog is closed
			if (result === 'saved') {
				// Optionally refresh data or show success message
				console.log(`Paytypes saved for ${day.FullName}`);
			}
		});
	}

	getPayTypesTooltip(payTypeString: string): string {
		if (!payTypeString || payTypeString.trim() === '') {
			return 'No pay types selected';
		}
		// Split comma-separated paytypes and format them nicely
		const paytypes = payTypeString
			.split(',')
			.map((pt) => pt.trim())
			.filter((pt) => pt.length > 0);
		return paytypes.length > 0
			? paytypes.join(', ')
			: 'No pay types selected';
	}

	checkFormStatus() {
		const isFormDirty: any =
			this.maintenanceForm.dirty || this.maintenanceForm.touched;
		this.formStatus.emit(isFormDirty);
	}

	checkIfExists(commaSeparatedString: string, value: string): boolean {
		const valuesArray = !!commaSeparatedString
			? commaSeparatedString?.split(',')?.map(function (item) {
					return item.trim().toLowerCase(); // Normalize to lower case
				})
			: [];
		return valuesArray.includes(value.trim().toLowerCase()); // Check for case-insensitive match
	}

	getAllPayTypes() {
		this.profileService.getAllPayTypes().subscribe({
			next: (d) => {
				this.allPaytypes = d.map((item: any) => ({
					value: item.PayType,
					label: item.PayType
				}));
			},
			error: (error) => {
				console.log('Get all pay types : ', error);
			}
		});
	}

	getPayTypeHoursRestrictedList() {
		this.profileService.getPayTypeHoursRestrictedList().subscribe({
			next: (d) => {
				this.restrictedPayTypes = d.map((item: any) => ({
					value: item.PayType,
					label: item.PayType,
					Enable: item.Enable,
					PayHours: item.PayHours
				}));

				// Update the form if timeCardSettings are already loaded
				this.updatePayHoursRestrictedValuesInForm();
			},
			error: (error) => {
				console.log('Get pay type hours restricted list : ', error);
			}
		});
	}

	saveRestrictedPayTypesIfChanged() {
		const restrictedValues = this.maintenanceForm.get(
			'PayHoursRestrictedValues'
		)?.value;

		// Ensure restrictedValues is an array
		const restrictedValuesArray = Array.isArray(restrictedValues)
			? restrictedValues
			: [];

		// Build the complete payload with all restrictedPayTypes, marking which ones are enabled
		const payload: RestrictedPayType[] = this.restrictedPayTypes.map(
			(payType: any) => {
				// Check if this pay type is selected in the form
				const selectedItem = restrictedValuesArray.find((item: any) => {
					const optionValue = item.option
						? item.option.value
						: item.value;
					return optionValue === payType.value;
				});

				if (selectedItem) {
					// This pay type is selected, get the PayHours value
					const payHours =
						selectedItem.inputValue !== undefined
							? selectedItem.inputValue
							: payType.PayHours || 0;

					// If PayHours is 0 or empty, set Enable to false
					const isEnabled = payHours > 0;

					return {
						PayType: payType.value,
						Enable: isEnabled,
						PayHours: payHours
						// value: payType.value,
						// label: payType.label
					};
				} else {
					// This pay type is not selected, so Enable=false
					return {
						PayType: payType.value,
						Enable: false,
						PayHours: payType.PayHours || 0
						// value: payType.value,
						// label: payType.label
					};
				}
			}
		);

		// Call the API
		this.profileService.savePayTypeHoursRestricted(payload).subscribe({
			next: (response) => {
				console.log(
					'Restricted pay types saved successfully:',
					response
				);
			},
			error: (error) => {
				console.error('Error saving restricted pay types:', error);
				this.toastr.error(
					'Failed to save restricted pay types',
					'Error'
				);
			}
		});
	}

	saveMaintenance() {
		if (this.maintenanceForm.value) {
			if (this.timeCardSetting && this.weekdays.length > 0) {
				this.timeCardSetting.WorkWeekDays = this.weekdays
					.filter(function (day) {
						return day.checked;
					})
					.map(function (day) {
						return day.value + 1;
					})
					.join(',');
			} else {
				this.timeCardSetting.WorkWeekDays = '';
			}

			if (
				this.timeCardSetting &&
				Array.isArray(
					this.maintenanceForm.get('NotificationUnionUnits')?.value
				) &&
				this.maintenanceForm.get('NotificationUnionUnits')?.value
					?.length > 0
			) {
				this.timeCardSetting.NotificationUnionUnits =
					this.maintenanceForm
						.get('NotificationUnionUnits')
						?.value.map((item: any) => item.label)
						.join(',');
			} else {
				this.timeCardSetting.NotificationUnionUnits = '';
			}

			if (
				this.timeCardSetting &&
				this.allPaytypes.length &&
				Array.isArray(
					this.maintenanceForm.get('ConflictingPayTypes')?.value
				)
			) {
				this.timeCardSetting.ConflictingPayTypes = this.maintenanceForm
					.get('ConflictingPayTypes')
					?.value.map((item: any) => item.label)
					.join(',');
			} else {
				this.timeCardSetting.ConflictingPayTypes = '';
			}

			if (
				this.timeCardSetting &&
				this.w2PrivilegedEmployees.length &&
				Array.isArray(
					this.maintenanceForm.get('W2PrivilegedEmployeeList')?.value
				)
			) {
				this.timeCardSetting.W2PrivilegedEmployeeList =
					this.maintenanceForm
						.get('W2PrivilegedEmployeeList')
						?.value.map((item: any) => item.value)
						.join(',');
			} else {
				this.timeCardSetting.W2PrivilegedEmployeeList = '';
			}

			// Create a copy of the form values to ensure we send the most up-to-date values
			const formValues = {
				...this.maintenanceForm.getRawValue(),
				RegularPayTypeAlias:
					this.maintenanceForm.get('RegularPayTypeAlias')?.value ??
					'',
				TrainingAdminEmpId: Array.isArray(
					this.maintenanceForm.get('TrainingAdminEmpId')?.value
				)
					? this.maintenanceForm
							.get('TrainingAdminEmpId')
							?.value.map((item: any) => item.value)
							.join(',')
					: '',
				CityworksAdminUserList: Array.isArray(
					this.maintenanceForm.get('CityworksAdminUserList')?.value
				)
					? this.maintenanceForm
							.get('CityworksAdminUserList')
							?.value.map((item: any) => item.value)
							.join(',')
					: '',
				GLLookUpEmployeeList: Array.isArray(
					this.maintenanceForm.get('GLLookUpEmployeeList')?.value
				)
					? this.maintenanceForm
							.get('GLLookUpEmployeeList')
							?.value.map((item: any) => item.value)
							.join(',')
					: '',
				WorkWeekDays: this.timeCardSetting.WorkWeekDays,
				NotificationUnionUnits:
					this.timeCardSetting.NotificationUnionUnits,
				ConflictingPayTypes: this.timeCardSetting.ConflictingPayTypes,
				W2PrivilegedEmployeeList:
					this.timeCardSetting.W2PrivilegedEmployeeList
			};

			// Check if PayHoursRestrictedValues has changed and call separate API
			this.saveRestrictedPayTypesIfChanged();

			this.homeService.saveTCKMaintenanceData(formValues).subscribe({
				next: (response) => {
					const storedUser = localStorage.getItem('user');
					const userData = storedUser ? JSON.parse(storedUser) : null;

					let previousLocationStatus =
						userData?.tailoring?.LocationServices;

					// Check if current user is in the training admin list
					const trainingAdminIds =
						this.maintenanceForm.value.TrainingAdminEmpId || [];
					const currentEmployeeNumber =
						userData?.credentials?.EmployeeNumber;
					const isCurrentUserTrainingAdmin = Array.isArray(
						trainingAdminIds
					)
						? trainingAdminIds.some(
								(admin: any) =>
									admin.value == currentEmployeeNumber
							)
						: trainingAdminIds
								.toString()
								.split(',')
								.includes(currentEmployeeNumber?.toString());

					// Set IsTraining from form value
					userData.IsTraining =
						this.maintenanceForm.get('IsTraining')?.value;

					// Set Cityworks admin properties
					const cityworksAdminIds =
						this.maintenanceForm.value.CityworksAdminUserList || [];
					const isCurrentUserCityworksAdmin = Array.isArray(
						cityworksAdminIds
					)
						? cityworksAdminIds.some(
								(admin: any) =>
									admin.value == currentEmployeeNumber
							)
						: cityworksAdminIds
								.toString()
								.split(',')
								.includes(currentEmployeeNumber?.toString());

					if (isCurrentUserCityworksAdmin) {
						userData.IsCityworksAdmin = true;
						userData.Cityworks = true;
						userData.CityworksAPIKEY =
							this.maintenanceForm.get('CityworksAPIKEY')?.value;
					}

					// Set training admin properties
					if (isCurrentUserTrainingAdmin) {
						userData.IsTrainingAdmin = true;
						userData.MailSettings = true;
						userData.TrainingCourse = true;
						userData.CourseNotifications = true;
						userData.TrainingGroup = true;
						userData.EmployeeTraining = true;
						userData.EmployeeNotifications = true;
					}

					// CourseNotifications should be true if user has training access (either IsTraining or IsTrainingAdmin)
					// userData.CourseNotifications = userData.IsTraining || userData.IsTrainingAdmin;
					userData.ConcurrentLogin =
						this.maintenanceForm.get('ConcurrentLogin')?.value ??
						false;
					userData.IsTimeCardLock = this.checkIfExists(
						this.maintenanceForm.value.TimeCardLockEmployeeList,
						userData?.credentials?.EmployeeNumber
					);
					userData.CopyDetailsDefaultEquipCheck =
						this.maintenanceForm.get(
							'CopyDetailsDefaultEquipCheck'
						)?.value;
					userData.CopyDetailsDefaultInvCheck =
						this.maintenanceForm.get(
							'CopyDetailsDefaultInvCheck'
						)?.value;
					// timecard
					userData.LaborWarningHours =
						this.maintenanceForm.value.LaborWarningHours;
					userData.EquipmentMaxHours =
						this.maintenanceForm.value.EquipmentMaxHours;
					userData.IsEquipmentMaxHoursWarning =
						this.maintenanceForm.get(
							'IsEquipmentMaxHoursWarning'
						)?.value;
					userData.IsLaborHoursWarning = this.maintenanceForm.get(
						'IsLaborHoursWarning'
					)?.value;
					userData.IsLaborUpdateEquipmentHoursWarning =
						this.maintenanceForm.get(
							'IsLaborUpdateEquipmentHoursWarning'
						)?.value;
					userData.IsEquipmentandLaborHoursWarning =
						this.maintenanceForm.get(
							'IsEquipmentandLaborHoursWarning'
						)?.value;
					userData.IsReviewShowTimecard = this.maintenanceForm.get(
						'IsReviewShowTimecard'
					)?.value;
					userData.IsAllowReviewersSubmit = this.maintenanceForm.get(
						'IsAllowReviewersSubmit'
					)?.value;
					userData.IsEquipmentNotLaborHoursWarning =
						this.maintenanceForm.get(
							'IsEquipmentNotLaborHoursWarning'
						)?.value;
					userData.IsEmpSummaryJoClassificationToggle =
						this.maintenanceForm.get(
							'IsEmpSummaryJoClassificationToggle'
						)?.value;
					userData.IsEmpSummaryExpandAllToggle =
						this.maintenanceForm.get(
							'IsEmpSummaryExpandAllToggle'
						)?.value;
					userData.IsEmpSummaryTrimGLToggle =
						this.maintenanceForm.get(
							'IsEmpSummaryTrimGLToggle'
						)?.value;
					userData.IsConsecutiveLoginFailedLock =
						this.maintenanceForm.get(
							'IsConsecutiveLoginFailedLock'
						)?.value;
					userData.RegularPayTypeAlias =
						this.maintenanceForm.get('RegularPayTypeAlias')
							?.value ?? '';
					userData.Cityworks =
						this.maintenanceForm.get('Cityworks')?.value;
					userData.CityworksAPIKEY =
						this.maintenanceForm.get('CityworksAPIKEY')?.value;
					userData.IsVUEWorksAPIEnabled =
						this.maintenanceForm.get('IsVUEWorksAPIEnabled')?.value;
					userData.VUEWorksAPIUrl =
						this.maintenanceForm.get('VUEWorksAPIUrl')?.value;
					userData.IsPTO = this.maintenanceForm.get('IsPTO')?.value;
					userData.IsNavItemMaster =
						this.maintenanceForm.get('IsItemMaster')?.value;
					userData.IsNavInventory =
						this.maintenanceForm.get('IsInventory')?.value;
					userData.IsNavPurchaseOrder =
						this.maintenanceForm.get('IsPurchaseOrder')?.value;

					// Check if current user is in the GL Look Up Employee list
					const glLookUpEmployeeList =
						this.maintenanceForm.value.GLLookUpEmployeeList || [];
					const isCurrentUserInGLLookUp = Array.isArray(
						glLookUpEmployeeList
					)
						? glLookUpEmployeeList.some(
								(emp: any) => emp.value == currentEmployeeNumber
							)
						: glLookUpEmployeeList
								.toString()
								.split(',')
								.includes(currentEmployeeNumber?.toString());

					userData.GLLookUpEmployeeList = Array.isArray(
						this.maintenanceForm.get('GLLookUpEmployeeList')?.value
					)
						? this.maintenanceForm
								.get('GLLookUpEmployeeList')
								?.value.map((item: any) => item.value)
								.join(',')
						: '';

					// Set GL Look Up permissions if current user is in the list
					if (isCurrentUserInGLLookUp) {
						userData.IsGLLookUpAdmin = true;
						userData.GLLookupAccess = true;
						userData.GLReportsAccess = true;
						userData.GLAccountAccess = true;
					} else {
						userData.IsGLLookUpAdmin = false;
						userData.GLLookupAccess = false;
						userData.GLReportsAccess = false;
						userData.GLAccountAccess = false;
					}

					userData.IsReviewBulkAction =
						this.maintenanceForm.get('IsReviewBulkAction')?.value;
					userData.IsShowHomeBenefitBalances =
						this.maintenanceForm.get('IsShowHomeBenefitBalances')
							?.value === true;
					userData.IsShowReviewBenefitBalances =
						this.maintenanceForm.get('IsShowReviewBenefitBalances')
							?.value === true;

					// Set W2ReportEnabled and W2PrivilegedEmployeeList from form values
					userData.W2ReportEnabled = this.maintenanceForm.get('W2ReportEnabled')?.value || false;
					userData.W2AllowAnyTaxYear =
						this.maintenanceForm.get('W2AllowAnyTaxYear')?.value === true;
					userData.W2PrivilegedEmployeeList = this.timeCardSetting.W2PrivilegedEmployeeList || '';
					userData.IsActivity517flow =
						this.maintenanceForm.get('IsActivity517flow')?.value === true;

					const updatedUserData = {
						...userData,
						complexPasswordRequirement:
							this.maintenanceForm.get('ComplexPassword')?.value,
						tailoring: {
							...userData.tailoring, // Ensure you preserve other properties of tailoring
							CombinedPayTypeEntry: this.maintenanceForm.get(
								'useCombinedPayTypeEntry'
							)?.value,
							ShowAdditionalHourlyRateApplies:
								this.maintenanceForm.get(
									'ShowAdditionalHourlyRateApplies'
								)?.value,
							SecondApproval:
								this.maintenanceForm.get('SecondApproval')
									?.value,
							ShowAttachments:
								this.maintenanceForm.get('ShowAttachments')
									?.value,
							TimeClock:
								this.maintenanceForm.get('TimeClock')?.value,
							Inventory:
								this.maintenanceForm.get('Inventory')?.value,
							TimeClockDecimal:
								this.maintenanceForm.get('TimeClockDecimal')
									?.value,
							TimeClockDecimalTenths: this.maintenanceForm.get(
								'TimeClockDecimalTenths'
							)?.value,
							LocationServices: this.maintenanceForm.get(
								'useLocationServices'
							)?.value,
							AllowZeroLaborHours: this.maintenanceForm.get(
								'AllowZeroLaborHours'
							)?.value,
							WorkWeekDays: this.timeCardSetting.WorkWeekDays,
							CopyDetailsNotAllowPayTypes:
								this.maintenanceForm.get(
									'CopyDetailsNotAllowPayTypes'
								)?.value ?? '',
							AllowChangePassword: this.maintenanceForm.get(
								'AllowChangePassword'
							)?.value,
							LoginScreenShowAll:
								this.maintenanceForm.get('LoginScreenShowAll')
									?.value
						}
					};
					this.authService.updateUserDataFromMaintenance(
						updatedUserData
					);
					// CRITICAL: Also refresh HomeStateService profile data to ensure sidebar updates
					// The sidebar subscribes to both authService.currentUser$ AND homeStateService.profileSettings$
					// so we need to update both services for immediate UI synchronization
					const employeeNumberForRefresh =
						updatedUserData?.credentials?.EmployeeNumber;
					if (employeeNumberForRefresh) {
						this.homeStateService
							.refreshProfileData(employeeNumberForRefresh, true)
							.subscribe({
								next: () => {
									console.log(
										'Profile data refreshed in HomeStateService after maintenance save'
									);
								},
								error: (error) => {
									console.warn(
										'Error refreshing profile data in HomeStateService:',
										error
									);
								}
							});
					}

					// Force change detection to update UI immediately
					this.cdr.detectChanges();

					// Additional step to ensure all components are updated
					// This is particularly important for sidebar and home page components
					setTimeout(() => {
						this.cdr.detectChanges();
					}, 50);

					this.toastr.success(
						'Maintenance data UPDATED successfully.',
						'Success',
						{
							positionClass: 'toast-bottom-left',
							toastClass: 'toast-icon custom-toast-success'
						}
					);

					// Update timeCardSetting.CityworksAdminUserList after save for real-time updates
					const cityworksAdminValue = this.maintenanceForm.get(
						'CityworksAdminUserList'
					)?.value;
					let cityworksAdminListString = '';
					if (Array.isArray(cityworksAdminValue)) {
						cityworksAdminListString = cityworksAdminValue
							.map((item: any) => item.value)
							.join(',');
						this.timeCardSetting.CityworksAdminUserList =
							cityworksAdminListString;
					} else {
						this.timeCardSetting.CityworksAdminUserList = '';
					}
					// Emit the updated CityworksAdminUserList to parent component
					this.cityworksAdminUserListUpdated.emit(
						cityworksAdminListString
					);

					// Emit integration status changes only after successful save
					const cityworksValue =
						this.maintenanceForm.get('Cityworks')?.value;
					const ptoValue = this.maintenanceForm.get('IsPTO')?.value;
					this.citiworksIntegration.emit(cityworksValue);
					this.ptoIntegration.emit(ptoValue);

					// Force emit the integration values after a short delay to ensure all components receive it
					setTimeout(() => {
						this.citiworksIntegration.emit(cityworksValue);
						this.ptoIntegration.emit(ptoValue);
					}, 100);

					// Mark form as pristine after successful save
					this.maintenanceForm.markAsPristine();
					this.initialFormValues = this.maintenanceForm.value;

					if (
						previousLocationStatus !==
						this.maintenanceForm.get('useLocationServices')?.value
					) {
						setTimeout(() => {
							window.location.reload();
						}, 10);
					}
				},
				error: (error) => {
					console.log('get tck master error : ', error);
				}
			});
		}
	}
}
